import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ArticleCategory } from '@/lib/types/admin';

const COLLECTION = 'articleCategories';

// Get all article categories
export const getArticleCategories = async (): Promise<ArticleCategory[]> => {
  const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ArticleCategory[];
};

// Get active article categories only (for public use)
export const getActiveArticleCategories = async (): Promise<ArticleCategory[]> => {
  const q = query(
    collection(db, COLLECTION),
    where('active', '==', true),
    orderBy('order', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ArticleCategory[];
};

// Create article category
export const createArticleCategory = async (
  label: string,
  order: number = 0
): Promise<string> => {
  const value = generateCategoryValue(label);

  const docRef = await addDoc(collection(db, COLLECTION), {
    value,
    label,
    active: true,
    order,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// Update article category
export const updateArticleCategory = async (
  id: string,
  data: Partial<{ label: string; value: string; active: boolean; order: number }>
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  const updateData: Record<string, unknown> = {
    ...data,
    updatedAt: Timestamp.now(),
  };

  // If label is being updated, also update value
  if (data.label && !data.value) {
    updateData.value = generateCategoryValue(data.label);
  }

  await updateDoc(docRef, updateData);
};

// Delete article category
export const deleteArticleCategory = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await deleteDoc(docRef);
};

// Toggle article category active status
export const toggleArticleCategoryActive = async (
  id: string,
  active: boolean
): Promise<void> => {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    active,
    updatedAt: Timestamp.now(),
  });
};

// Generate URL-friendly value from label
export const generateCategoryValue = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};
